import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Search as SearchIcon, X } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ProductCard } from "../components/ProductCard";
import { SEO } from "../components/SEO";
import { getCategories, getProducts } from "../lib/api";

export default function AllProducts() {
  const [sp, setSp] = useSearchParams();
  const term = (sp.get("search") || "").trim();
  const [cats, setCats] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [term]);

  useEffect(() => {
    if (term) return; // browsing mode → load categories only
    getCategories().then(setCats).catch(() => {});
  }, [term]);

  useEffect(() => {
    if (!term) { setResults([]); return; }
    setLoading(true);
    getProducts({ search: term })
      .then((data) => setResults(data || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [term]);

  const clearSearch = () => { const next = new URLSearchParams(sp); next.delete("search"); setSp(next); };

  // ==== SEARCH RESULTS MODE ====
  if (term) {
    return (
      <main data-testid="search-results-page" className="pt-[104px]">
        <SEO
          title={`Search: ${term} | VR Packaging`}
          description={`Wholesale ${term} products from VR Packaging & Hygiene NZ. Browse matching SKUs and request a trade quote — nationwide dispatch.`}
          path={`/products?search=${encodeURIComponent(term)}`}
        />
        <div className="bg-vr-navy text-white">
          <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-14">
            <Breadcrumbs items={[{ label: "Products", to: "/products" }, { label: `Search: ${term}` }]} />
            <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="flex items-center gap-3 text-vr-hygiene text-xs uppercase tracking-[0.2em] font-semibold">
                  <SearchIcon size={15} /> Search results
                </div>
                <h1 className="mt-3 font-display font-extrabold tracking-tight text-3xl md:text-5xl">“{term}”</h1>
                <p data-testid="search-result-count" className="mt-3 text-white/70">
                  {loading ? "Searching…" : `${results.length} product${results.length === 1 ? "" : "s"} matched`}
                </p>
              </div>
              <button
                type="button"
                onClick={clearSearch}
                data-testid="search-clear-btn"
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X size={15} /> Clear search
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
          {loading ? (
            <div data-testid="search-loading" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-vr-border bg-white overflow-hidden animate-pulse">
                  <div className="aspect-square bg-vr-bg2" />
                  <div className="p-5 space-y-2">
                    <div className="h-3 w-16 bg-vr-bg2 rounded" />
                    <div className="h-4 w-3/4 bg-vr-bg2 rounded" />
                    <div className="h-3 w-1/2 bg-vr-bg2 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div data-testid="search-empty" className="max-w-xl mx-auto text-center py-16">
              <div className="mx-auto w-16 h-16 rounded-full bg-vr-bg2 grid place-items-center text-vr-dim">
                <SearchIcon size={26} />
              </div>
              <h2 className="mt-6 font-display font-bold text-2xl text-vr-ink">No products matched “{term}”</h2>
              <p className="mt-3 text-vr-dim">Try a broader term, check the spelling, or browse our categories below.</p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Link to="/products" data-testid="search-empty-browse" onClick={clearSearch} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors">
                  Browse all categories
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-vr-border text-vr-ink font-semibold hover:border-vr-brand hover:text-vr-brand transition-colors">
                  Ask us for a quote
                </Link>
              </div>
            </div>
          ) : (
            <div data-testid="search-results-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {results.map((p, i) => (
                <ProductCard key={p.sku} p={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </main>
    );
  }

  // ==== DEFAULT BROWSE MODE ====
  return (
    <main data-testid="all-products-page" className="pt-[104px]">
      <SEO
        title="All Product Categories | VR Packaging & Hygiene NZ"
        description="Browse VR Packaging & Hygiene's complete wholesale range — 10 categories covering food service, hand protection, cleaning chemicals, paper hygiene, waste disposal and more."
        path="/products"
      />
      <div className="bg-vr-navy text-white">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-14">
          <Breadcrumbs items={[{ label: "All Categories" }]} />
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl md:text-6xl">All Categories</h1>
          <p className="mt-4 text-white/70 max-w-2xl">Browse our complete wholesale range across packaging, hygiene, cleaning and safety — built for New Zealand businesses.</p>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cats.map((c, i) => (
            <motion.div key={c.slug} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: (i % 3) * 0.06 }}>
              <Link to={`/category/${c.slug}`} data-testid={`all-category-${c.slug}`} className="group block rounded-3xl border border-vr-border bg-white overflow-hidden hover:border-vr-brand hover:shadow-2xl transition-all duration-300">
                <div className="relative aspect-[4/3] overflow-hidden bg-white">
                  <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full bg-white/90 text-vr-navy opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight size={18} />
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-display font-bold text-xl text-vr-ink group-hover:text-vr-brand transition-colors">{c.name}</h2>
                    {c.product_count > 0 && <span className="text-xs text-vr-dim bg-vr-bg2 rounded-full px-2.5 py-1">{c.product_count} items</span>}
                  </div>
                  <p className="mt-2 text-sm text-vr-dim leading-relaxed line-clamp-2">{c.description}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[["Nationwide Delivery", "Fast dispatch NZ-wide"], ["Wholesale Pricing", "Genuine trade rates"], ["Wide Range", "10 categories, 1 account"], ["Trusted Supplier", "NZ-owned & operated"]].map(([t, s]) => (
            <div key={t} className="rounded-2xl bg-vr-bg2 border border-vr-border p-6 text-center">
              <div className="font-display font-bold text-vr-navy">{t}</div>
              <div className="mt-1 text-sm text-vr-dim">{s}</div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
